import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Chip, { type ChipProps } from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";

import { DEFAULT_TEAM_COLOR } from "services/types/teams.type";

export type TeamPillProps = {
  name: string;
  /** The team's own colour (`Team.color`); tints the pill so a team's work is spotted at a glance. */
  color?: string | null;
  size?: ChipProps["size"];
  sx?: ChipProps["sx"];
  onClick?: ChipProps["onClick"];
  onDelete?: ChipProps["onDelete"];
  testId?: string;
};

/**
 * A team as a pill: on tasks, mention rows, the @ list, Home → For me and Administration → Teams.
 *
 * Every team pill carries the two-person mark, so it is never mistaken for a tag (lilac filled
 * chip) or a person (plain name). Colours are set through `sx`, not the `color` prop: the chip theme
 * flattens every filled chip to one lilac background (see root CLAUDE.md). The pill keeps one line
 * (`whiteSpace: nowrap`) so it never wraps inside a table cell.
 */
export default function TeamPill({
  name,
  color,
  size = "small",
  sx,
  onClick,
  onDelete,
  testId = "team-pill"
}: TeamPillProps) {
  const tint = color || DEFAULT_TEAM_COLOR;
  return (
    <Chip
      size={size}
      variant="outlined"
      icon={
        <GroupsOutlinedIcon aria-hidden sx={{ color: `${tint} !important` }} />
      }
      label={name}
      onClick={onClick}
      onDelete={onDelete}
      data-testid={testId}
      aria-label={`Team ${name}`}
      sx={{
        color: tint,
        borderColor: alpha(tint, 0.45),
        backgroundColor: alpha(tint, 0.1),
        fontWeight: 600,
        whiteSpace: "nowrap",
        maxWidth: "100%",
        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
        ...sx
      }}
    />
  );
}
